import React, { useState, useEffect } from 'react';
import { 
    Trophy, ChevronLeft, Loader2, 
    CheckCircle2, AlertCircle, X,
    Brain, Timer, User, ChevronRight
} from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';
import { useAuth } from '../../../context/AuthContext';
import { gameService } from '../../../services/gameService';
import { pointService } from '../../../services/pointService';
import { getTeamName } from '../../../services/teamService';
import { fetchMatchDetails } from '../../../services/footballService';
import { TotoRound, TotoMatch, TotoPrediction } from '../../../types';
import { t, tr } from '../../../i18n';
import SEO from '../../Common/SEO';

interface TotoResultViewProps {
    onBack: () => void;
}

export const TotoResultView: React.FC<TotoResultViewProps> = ({ onBack }) => {
    const { lang } = useLanguage();
    const { user } = useAuth();
    
    const [rounds, setRounds] = useState<TotoRound[]>([]);
    const [selectedRound, setSelectedRound] = useState<TotoRound | null>(null);
    const [matches, setMatches] = useState<TotoMatch[]>([]);
    const [prediction, setPrediction] = useState<TotoPrediction | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setIsLoading(true);
        try {
            const roundData = await gameService.getTotoRounds();
            setRounds(roundData);
            if (roundData.length > 0) {
                handleSelectRound(roundData[0]);
            }
        } catch (error) {
            console.error('Failed to load results:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSelectRound = async (round: TotoRound) => {
        setSelectedRound(round);
        try {
            const [matchData, predData] = await Promise.all([
                gameService.getTotoMatches(round.id),
                user ? gameService.getUserTotoPrediction(round.id, user.id) : Promise.resolve(null)
            ]);

            setMatches(matchData);
            
            // All matches finished check
            const allFinished = matchData.length > 0 && matchData.every(m => m.result);
            if (allFinished && round.status !== 'finished') {
                setSelectedRound({ ...round, status: 'finished' });
            }

            // Calculate real-time correct count if prediction exists
            if (predData && matchData.length > 0) {
                let currentCorrectCount = 0;
                matchData.forEach(match => {
                    const userChoices = predData.predictions[match.match_id] || [];
                    if (match.result && userChoices.includes(match.result)) {
                        currentCorrectCount++;
                    }
                });
                
                const updatedPrediction = {
                    ...predData,
                    correct_count: currentCorrectCount
                };
                setPrediction(updatedPrediction);

                // Handle rewards: Earn 10 points and 10 score per correct prediction
                if (user && updatedPrediction.correct_count > 0 && !updatedPrediction.is_rewarded && allFinished) {
                    const rewardAmount = updatedPrediction.correct_count * 10;
                    
                    // Award points
                    await gameService.updateTotoPredictionRewardStatus(updatedPrediction.id, true);
                    await pointService.awardPoints(user.id, rewardAmount, `Triple Bet Reward - Round ${round.round_number}`);
                    
                    // Award score for ranking
                    const games = await gameService.getStoreGames();
                    const totoGame = games.find(g => g.title.toUpperCase().includes('TRIPLE BET') || g.link.includes('toto'));
                    if (totoGame) {
                        await gameService.saveScore(totoGame.id, rewardAmount);
                    }

                    // Update local state to prevent multiple reward calls
                    setPrediction({ ...updatedPrediction, is_rewarded: true });
                }
            } else {
                setPrediction(predData);
            }
        } catch (error) {
            console.error('Failed to load round details:', error);
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-3">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                <p className="text-xs font-black text-slate-400 uppercase tracking-widest">{t(lang, 'loading')}</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 pb-20">
            <SEO 
                title={t(lang, 'toto_result_seo_title')}
                description={t(lang, 'toto_result_seo_description')}
                keywords={t(lang, 'toto_result_seo_keywords')}
            />
            <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-slate-100 px-6 py-4">
                <div className="max-w-2xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button onClick={onBack} className="p-2 -ml-2 text-slate-400 hover:text-slate-900 transition-colors">
                            <ChevronLeft size={24} />
                        </button>
                        <h1 className="text-lg font-black text-slate-900 uppercase tracking-tight">
                            {t(lang, 'toto_check_predictions')}
                        </h1>
                    </div>
                </div>
            </header>

            <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
                {/* Round Selector */}
                <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2">
                    {rounds.map(round => (
                        <button
                            key={round.id}
                            onClick={() => handleSelectRound(round)}
                            className={`px-5 py-2.5 rounded-2xl text-xs font-black whitespace-nowrap transition-all ${
                                selectedRound?.id === round.id
                                ? 'bg-blue-600 text-white shadow-md shadow-blue-100'
                                : 'bg-white text-slate-400 border border-slate-100 hover:bg-slate-50'
                            }`}
                        >
                            {tr(lang, 'toto_round_format', { n: round.round_number })}
                        </button>
                    ))}
                </div>

                {selectedRound && (
                    <div className="space-y-4">
                        <div className="bg-white rounded-[32px] p-6 border border-slate-100 shadow-sm">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-xl font-black text-slate-900 leading-tight">
                                    {selectedRound[`title_${lang}` as keyof TotoRound] || selectedRound.title_en}
                                </h2>
                                <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest ${
                                    selectedRound.status === 'finished' ? 'bg-slate-100 text-slate-500' : 'bg-green-100 text-green-600'
                                }`}>
                                    {selectedRound.status === 'finished' 
                                        ? t(lang, 'toto_finished') 
                                        : t(lang, 'toto_in_progress')}
                                </span>
                            </div>
                            
                            {prediction ? (
                                <div className="flex items-center gap-6 p-4 bg-slate-50 rounded-2xl">
                                    <div className="flex-1 text-center">
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{t(lang, 'toto_success_count')}</p>
                                        <p className="text-lg font-black text-blue-600">
                                            {matches.length > 0 && matches.some(m => m.result) 
                                                ? `${prediction.correct_count} / ${matches.length}` 
                                                : '-'}
                                        </p>
                                    </div>
                                    <div className="w-px h-8 bg-slate-200" />
                                    <div className="flex-1 text-center">
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{t(lang, 'toto_submitted_at')}</p>
                                        <p className="text-xs font-bold text-slate-700">
                                            {prediction.created_at ? new Date(prediction.created_at).toLocaleDateString() : '-'}
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className="p-8 text-center bg-slate-50 rounded-[32px] border border-dashed border-slate-200">
                                    <AlertCircle className="mx-auto text-slate-300 mb-2" size={32} />
                                    <p className="text-sm font-bold text-slate-400">
                                        {t(lang, 'toto_not_participated')}
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="space-y-3">
                            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] px-2 flex items-center gap-2">
                                <CheckCircle2 size={14} className="text-blue-500" />
                                {t(lang, 'toto_detailed_results')}
                            </h3>
                            {matches.map((match, idx) => {
                                const userChoices = prediction?.predictions[match.match_id] || [];
                                const isCorrect = match.result && userChoices.includes(match.result);
                                
                                return (
                                    <div 
                                        key={match.id} 
                                        onClick={() => {
                                            if (match.match_id) {
                                                window.navigateTo(`/game/${match.match_id}`);
                                            }
                                        }}
                                        className="bg-white rounded-[28px] p-5 border border-slate-100 shadow-sm relative overflow-hidden active:bg-slate-50 transition-colors cursor-pointer group"
                                    >
                                        {match.result && prediction && (
                                            <div className={`absolute top-0 right-0 px-4 py-1.5 rounded-bl-2xl text-[10px] font-black uppercase tracking-widest ${
                                                isCorrect ? 'bg-green-500 text-white' : 'bg-rose-500 text-white'
                                            }`}>
                                                {isCorrect ? t(lang, 'toto_correct') : t(lang, 'toto_incorrect')}
                                            </div>
                                        )}

                                        <div className="flex items-center justify-between mb-4">
                                            <div className="flex items-center gap-2">
                                                <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest">Game {idx + 1}</span>
                                                <span className="text-[10px] font-bold text-slate-400">{match.match_time}</span>
                                            </div>
                                        </div>

                                        <div className="flex items-center justify-between gap-4 mb-6 relative">
                                            <div className="absolute inset-y-0 -right-2 flex items-center">
                                                <div className="w-8 h-8 rounded-full bg-slate-50 flex items-center justify-center text-slate-300 group-hover:bg-blue-50 group-hover:text-blue-500 transition-all">
                                                    <ChevronRight size={16} />
                                                </div>
                                            </div>
                                            <div className="flex flex-col items-center gap-2 flex-1 text-center">
                                                <img src={match.home_team_logo} alt="" className="w-10 h-10 object-contain" />
                                                <span className="text-xs font-black text-slate-900 truncate w-full">
                                                    {getTeamName(match.home_team_id, lang, match.home_team_name)}
                                                </span>
                                            </div>
                                            <div className="flex flex-col items-center gap-1">
                                                <span className="text-[10px] font-black text-slate-300 uppercase">VS</span>
                                            </div>
                                            <div className="flex flex-col items-center gap-2 flex-1 text-center">
                                                <img src={match.away_team_logo} alt="" className="w-10 h-10 object-contain" />
                                                <span className="text-xs font-black text-slate-900 truncate w-full">
                                                    {getTeamName(match.away_team_id, lang, match.away_team_name)}
                                                </span>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-3 gap-2">
                                            {[
                                                { id: '1', label: t(lang, 'toto_win') },
                                                { id: 'X', label: t(lang, 'toto_draw') },
                                                { id: '2', label: t(lang, 'toto_loss') }
                                            ].map(choice => (
                                                <div
                                                    key={choice.id}
                                                    className={`py-2.5 rounded-xl text-[10px] font-black text-center transition-all ${
                                                        userChoices.includes(choice.id as any)
                                                        ? 'bg-blue-600 text-white shadow-md'
                                                        : match.result === choice.id
                                                        ? 'bg-slate-900 text-white'
                                                        : 'bg-slate-50 text-slate-300'
                                                    }`}
                                                >
                                                    {choice.label}
                                                    {userChoices.includes(choice.id as any) && (
                                                        <span className="ml-1 text-[8px] opacity-70">
                                                            {t(lang, 'toto_my_pick')}
                                                        </span>
                                                    )}
                                                    {match.result === choice.id && (
                                                        <span className="ml-1 text-[8px] opacity-70">
                                                            {t(lang, 'toto_result')}
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};
